import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { Pill } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { contractStatus, dateLabel, money, customerShort } from '../../lib/format';
import { ContractEditor } from './ContractEditor';
import { ContractDetail } from './ContractDetail';
import type { Contract } from '../../lib/types';

export function ContractsPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const canWrite = can(user, 'contracts', 'create');
  const canPrint = can(user, 'contracts', 'print');
  const [reloadSignal, setReloadSignal] = useState(0);
  // mode: 关闭 / 新建 / 编辑（携带待编辑草稿合同）
  const [editor, setEditor] = useState<{ mode: 'new' } | { mode: 'edit'; c: Contract } | null>(null);
  // 行级「编辑」正在拉取全量单据的合同 id（列表行是投影、不含明细行，必须先取详情再开编辑器）
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);

  const refresh = () => setReloadSignal((n) => n + 1);

  // 行级编辑入口：拉全量（含 lines）→ 开模块编辑器。失败不开编辑器——
  // 用投影行开编辑器会得到一条空明细，用户若保存会用空行覆盖真实明细。
  const openRowEdit = async (row: Contract) => {
    setEditLoadingId(row.id);
    try {
      const full = await api.detail<Contract>(`/api/contracts/${row.id}`);
      setEditor({ mode: 'edit', c: full });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.alert('读取合同明细失败，请稍后重试或在列表展开该行后点「编辑草稿」');
    } finally {
      setEditLoadingId(null);
    }
  };

  const columns: Column<Contract>[] = [
    { header: '合同编号', render: (c) => <span className="num strong">{c.number}</span> },
    // 「客户合同号」= 客户自己那一侧的合同/订单号（销售合同上的 po_no）。
    // 紧挨「合同编号」放：前者是我们的号、后者是对方的号，对读/对账时一眼能比对。
    // 该列多数行会是空的 —— 新系统允许合同确认后补录（PATCH /api/contracts/:id 的 META_FIELDS 含 po_no），
    // 历史 ERPNext 继承单一般不补（票面不印）。空值显示「—」而不是隐藏列，缺数据要看得见。
    { header: '客户合同号', render: (c) => <span className="num clip" style={{ maxWidth: '18ch' }} title={c.po_no || ''}>{c.po_no || '—'}</span> },
    // 客户名是列表里最需要辨认的一列：20ch 会把「FLOWBACK SERVICES S.R.L.」
    // 截成「FLOWBACK SERVICES S...」。放宽到 26ch，超长的仍截断并保留 title。
    { header: '客户', render: (c) => <span title={c.customer_name || ''}>{customerShort(c.customer_name)}</span> },
    { header: '合同日期', render: (c) => <span className="num">{dateLabel(c.contract_date)}</span> },
    // 「交付期限」列已移除（2026-10-29 现场要求）：它与「交货日期」语义接近，
    // 两者并排占两列却常常一个空一个满，读者要在两列间来回比对。
    // 交付期限仍在详情页的 kv-grid 里完整展示，列表只保留信息量更高的一列。
    { header: '交货日期', render: (c) => <span className="num muted">{dateLabel(c.last_delivery_date)}</span> },
    { header: '含税总额', align: 'right', render: (c) => <span className="num strong">{money(c.grand_total, c.currency)}</span> },
    // 「来源」用单字缩写（2026-10-29 现场要求：改为图标或缩短文字）。
    // 原「报价转入 / ERPNext / 手工新建」占 4-5 个汉字宽，是 10 列里最不划算的一列。
    // 缩写保留可辨识性，完整含义放 title：鼠标悬停即可看到，信息没丢。
    { header: '来源', render: (c) => {
      const src = c.quotation_id
        ? { short: '报价', full: '报价转入' }
        : c.legacy_erpnext_id
          ? { short: '旧', full: 'ERPNext 历史继承' }
          : { short: '手', full: '手工新建' };
      return <span className="muted" title={src.full}>{src.short}</span>;
    } },
    { header: '状态', render: (c) => { const s = contractStatus(c.status); return <Pill tone={s.tone} label={s.label} />; } },
  ];

  return (
    <>
      {editor ? (
        <ContractEditor
          initial={editor.mode === 'edit' ? editor.c : null}
          onCancel={() => setEditor(null)}
          onSaved={(saved) => {
            setEditor(null);
            refresh();
            // 让被保存的合同号进入列表搜索，方便定位结果。
            const url = new URL(window.location.href);
            url.searchParams.set('q', saved.number);
            window.history.replaceState({}, '', url.toString());
          }}
        />
      ) : null}

      <ListPage<Contract>
        title="销售合同"
        desc="销售合同 · /api/contracts，支持草稿新建/编辑、报价转合同快照、状态流转、交货进度与剩余可交、审计留痕，以及西语打印 / PDF（含 ERPNext 历史合同，当页展开，不用抽屉）"
        endpoint="/api/contracts"
        columns={columns}
        rowId={(c) => c.id}
        searchPlaceholder="搜索合同号 / 客户合同号 / 客户 / 状态 / 税号…"
        initialQuery={sp.get('q') || ''}
        reloadSignal={reloadSignal}
        rowActions={(row) => (
          <>
            {canWrite && !row.legacy_erpnext_id && row.status !== 'cancelled' ? (
              <button
                className="btn link"
                disabled={editLoadingId === row.id}
                title={`编辑合同 ${row.number}（表头 + 明细行均可改）`}
                onClick={(e) => { e.stopPropagation(); void openRowEdit(row); }}
              >
                {editLoadingId === row.id ? '载入中…' : '编辑'}
              </button>
            ) : null}
            <RecordActions type="contracts" row={row} fields={RECORD_FIELDS['contracts']} label={String(row.number)} hideEdit onDone={() => setReloadSignal((n) => n + 1)} />
          </>
        )}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setEditor((e) => (e && e.mode === 'new' ? null : { mode: 'new' }))}>
            {editor?.mode === 'new' ? '收起新建' : '新建合同草稿'}
          </button>
        ) : undefined}
        renderDetail={(row) => (
          <ContractDetail
            row={row}
            onEdit={(c) => setEditor({ mode: 'edit', c })}
            onChanged={refresh}
          />
        )}
      />
      {!canWrite ? (
        <div className="notice info" style={{ marginTop: 'var(--gap)' }}>
          当前角色为只读。销售合同的新建、编辑与状态流转由销售与管理员执行（服务端强制校验）。{canPrint ? '西语打印 / 导出 PDF 对你开放（含 ERPNext 历史合同）。' : '西语打印 / 导出 PDF 仅限管理员 / 销售 / 财务角色。'}
        </div>
      ) : null}
    </>
  );
}
